import { createStore, select, withProps } from '@ngneat/elf';
import { persistState, localStorageStrategy } from '@ngneat/elf-persist-state';
import { Injectable } from '@angular/core';
import { VALID_RESOLUTIONS, VideoSettings } from '../shared/constants/video-settings';

/**
 * A persisted device choice. Browsers rotate deviceId strings (Firefox
 * without persistent permission, Safari across permission transitions,
 * Chromium when site data is cleared), so the id alone is not a reliable
 * key — the name is kept alongside it for re-matching. Never pass the
 * stored id into a getUserMedia constraint directly; resolve it against
 * the live device list first (see DeviceService).
 */
export interface DeviceSelection {
  id: string;
  name: string;
}

export interface MediaStreamConfig {
  autoGainControl: boolean;
  echoCancellation: boolean;
  noiseSuppression: boolean;
  /** null = follow the browser/OS default device */
  audioInputDevice: DeviceSelection | null;
  /** null = follow the browser/OS default device */
  audioOutputDevice: DeviceSelection | null;
  /** null = follow the browser default camera */
  videoDevice: DeviceSelection | null;
  backgroundBlurEnabled: boolean;
  backgroundBlurAmount: number;
  showCameraPreview: boolean;
  cameraSettingsOverride: VideoSettings | null;
  screenSettingsOverride: VideoSettings | null;
}

/** Persisted shape before the {id, name} device-selection model. */
type LegacyMediaStreamConfig = MediaStreamConfig & {
  audioInputDeviceId?: string;
  audioOutputDeviceId?: string;
  videoDeviceId?: string;
};

const MEDIA_CONFIG_STORE_NAME = 'andorMediaSettings.v1';

const mediaConfigStore = createStore(
  { name: MEDIA_CONFIG_STORE_NAME },
  withProps<MediaStreamConfig>({
    autoGainControl: true,
    echoCancellation: true,
    noiseSuppression: true,
    audioInputDevice: null,
    audioOutputDevice: null,
    videoDevice: null,
    backgroundBlurEnabled: false,
    backgroundBlurAmount: 15,
    showCameraPreview: true,
    cameraSettingsOverride: null,
    screenSettingsOverride: null,
  })
);

/**
 * Upgrades a persisted config. Legacy plain-id fields become {id, name}
 * selections: the name is unknown at migration time ('' never name-matches);
 * the first successful id-resolution backfills it. Overrides saved with the
 * former 4:3 '480p' preset (640×480) move to its 16:9 successor, so the
 * settings still show '480p' as selected.
 */
export function migrateMediaConfig(value: MediaStreamConfig): MediaStreamConfig {
  const legacy = value as LegacyMediaStreamConfig;
  const migrateDevice = (id: string | undefined): DeviceSelection | null =>
    id ? { id, name: '' } : null;
  const { audioInputDeviceId, audioOutputDeviceId, videoDeviceId, ...rest } = legacy;
  return {
    ...rest,
    audioInputDevice: legacy.audioInputDevice ?? migrateDevice(audioInputDeviceId),
    audioOutputDevice: legacy.audioOutputDevice ?? migrateDevice(audioOutputDeviceId),
    videoDevice: legacy.videoDevice ?? migrateDevice(videoDeviceId),
    cameraSettingsOverride: migrateResolution(legacy.cameraSettingsOverride),
    screenSettingsOverride: migrateResolution(legacy.screenSettingsOverride),
  };
}

function migrateResolution(settings: VideoSettings | null | undefined): VideoSettings | null {
  if (!settings) return null;
  // Widened: the preset tuple is `as const`, so 640 is no longer a legal literal.
  const { width, height }: { width: number; height: number } = settings.resolution;
  return width === 640 && height === 480
    ? { ...settings, resolution: { ...VALID_RESOLUTIONS[0].value } }
    : settings;
}

persistState(mediaConfigStore, {
  key: MEDIA_CONFIG_STORE_NAME,
  storage: localStorageStrategy,
  preStoreInit: migrateMediaConfig,
});

@Injectable({
  providedIn: 'root',
})
export class MediaConfigStore {
  audioInputDevice$ = mediaConfigStore.pipe(select((state) => state.audioInputDevice));
  audioOutputDevice$ = mediaConfigStore.pipe(select((state) => state.audioOutputDevice));
  videoDevice$ = mediaConfigStore.pipe(select((state) => state.videoDevice));
  autoGainControl$ = mediaConfigStore.pipe(select((state) => state.autoGainControl));
  echoCancellation$ = mediaConfigStore.pipe(select((state) => state.echoCancellation));
  noiseSuppression$ = mediaConfigStore.pipe(select((state) => state.noiseSuppression));
  backgroundBlurEnabled$ = mediaConfigStore.pipe(select((state) => state.backgroundBlurEnabled));
  backgroundBlurAmount$ = mediaConfigStore.pipe(select((state) => state.backgroundBlurAmount));

  update(config: Partial<MediaStreamConfig>) {
    mediaConfigStore.update((state) => ({
      ...state,
      ...config,
    }));
  }

  get value() {
    return mediaConfigStore.getValue();
  }

  get store() {
    return mediaConfigStore;
  }
}
